import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface IdTokenClaims {
  aud?: string;
  iss?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  sub?: string;
  name?: string;
  email?: string;
  emails?: string[];
  tfp?: string;
  nonce?: string;
  scp?: string;
  azp?: string;
  ver?: string;
  [key: string]: unknown;
}

export interface UserPermissions {
  isPlatformAdmin: boolean;
  isTenantAdmin: boolean;
  isInstanceAdmin: boolean;
}

export interface ErrorInfo {
  isError: boolean;
  errorMessage: string;
  errorCode: string;
}

export interface CapabilityPermissions {
  allowView: boolean;
  allowEdit: boolean;
  allowDelete: boolean;
}

export interface InstanceSettings {
  cultureName: string | undefined;
  timezone: string | undefined;
}

export interface AuthState {
  initialized: boolean;
  accessToken: string | undefined;
  idTokenClaims: IdTokenClaims | undefined;
  permissions: UserPermissions;
  capabilities: CapabilityPermissions;
  error: ErrorInfo;
  isInactivePlan: boolean;
  isFullAccess: boolean;
  isAuthenticated: boolean;
  instanceSettings: InstanceSettings;
}

const initialState: AuthState = {
  initialized: false,
  accessToken: undefined,
  idTokenClaims: undefined,
  permissions: {
    isPlatformAdmin: false,
    isTenantAdmin: false,
    isInstanceAdmin: false,
  },
  capabilities: {
    allowView: false,
    allowEdit: false,
    allowDelete: false,
  },
  error: {
    isError: false,
    errorMessage: "",
    errorCode: "",
  },
  isInactivePlan: false,
  isFullAccess: false,
  isAuthenticated: false,
  instanceSettings: {
    cultureName: undefined,
    timezone: undefined,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string | undefined>) => {
      state.accessToken = action.payload;
    },

    setIdTokenClaims: (state, action: PayloadAction<IdTokenClaims | undefined>) => {
      state.idTokenClaims = action.payload;
    },

    setTokens: (
      state,
      action: PayloadAction<{
        accessToken: string | undefined;
        idTokenClaims: IdTokenClaims | undefined;
      }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.idTokenClaims = action.payload.idTokenClaims;
    },

    clearTokens: (state) => {
      state.accessToken = undefined;
      state.idTokenClaims = undefined;
      state.isAuthenticated = false;
    },

    setPermissions: (state, action: PayloadAction<UserPermissions>) => {
      state.permissions = action.payload;
    },

    setIsPlatformAdmin: (state, action: PayloadAction<boolean>) => {
      state.permissions.isPlatformAdmin = action.payload;
    },

    setIsTenantAdmin: (state, action: PayloadAction<boolean>) => {
      state.permissions.isTenantAdmin = action.payload;
    },

    setIsInstanceAdmin: (state, action: PayloadAction<boolean>) => {
      state.permissions.isInstanceAdmin = action.payload;
    },

    setError: (state, action: PayloadAction<ErrorInfo>) => {
      state.error = action.payload;
    },

    setIsError: (
      state,
      action: PayloadAction<boolean | { isError: boolean; message?: string; code?: string }>,
    ) => {
      if (typeof action.payload === "boolean") {
        state.error.isError = action.payload;
        if (!action.payload) {
          state.error.errorMessage = "";
          state.error.errorCode = "";
        }
      } else {
        state.error.isError = action.payload.isError;
        state.error.errorMessage = action.payload.message ?? "";
        state.error.errorCode = action.payload.code ?? "";
      }
    },

    setIsInactivePlan: (state, action: PayloadAction<boolean>) => {
      state.isInactivePlan = action.payload;
    },

    setIsFullAccess: (state, action: PayloadAction<boolean>) => {
      state.isFullAccess = action.payload;
    },

    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },

    setCapabilities: (state, action: PayloadAction<CapabilityPermissions>) => {
      state.capabilities = action.payload;
    },

    setAllowView: (state, action: PayloadAction<boolean>) => {
      state.capabilities.allowView = action.payload;
    },

    setAllowEdit: (state, action: PayloadAction<boolean>) => {
      state.capabilities.allowEdit = action.payload;
    },

    setAllowDelete: (state, action: PayloadAction<boolean>) => {
      state.capabilities.allowDelete = action.payload;
    },

    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },

    signOut: (state) => {
      state.accessToken = undefined;
      state.idTokenClaims = undefined;
      state.permissions = {
        isPlatformAdmin: false,
        isTenantAdmin: false,
        isInstanceAdmin: false,
      };
      state.capabilities = {
        allowView: false,
        allowEdit: false,
        allowDelete: false,
      };
      state.error = {
        isError: false,
        errorMessage: "",
        errorCode: "",
      };
      state.isInactivePlan = false;
      state.isFullAccess = false;
      state.isAuthenticated = false;
      state.initialized = false;
    },

    tenantChange: (state) => {
      state.permissions = {
        isPlatformAdmin: state.permissions.isPlatformAdmin,
        isTenantAdmin: false,
        isInstanceAdmin: false,
      };
    },

    instanceChange: (state) => {
      state.permissions.isInstanceAdmin = false;
    },

    setInstanceSettings: (state, action: PayloadAction<InstanceSettings>) => {
      state.instanceSettings = action.payload;
    },

    setInstanceCultureName: (state, action: PayloadAction<string | undefined>) => {
      state.instanceSettings.cultureName = action.payload;
    },

    setInstanceTimezone: (state, action: PayloadAction<string | undefined>) => {
      state.instanceSettings.timezone = action.payload;
    },
  },
});

export const {
  setAccessToken,
  setIdTokenClaims,
  setTokens,
  clearTokens,
  setPermissions,
  setIsPlatformAdmin,
  setIsTenantAdmin,
  setIsInstanceAdmin,
  setError,
  setIsError,
  setIsInactivePlan,
  setIsFullAccess,
  setIsAuthenticated,
  setCapabilities,
  setAllowView,
  setAllowEdit,
  setAllowDelete,
  setInitialized,
  signOut,
  tenantChange,
  instanceChange,
  setInstanceSettings,
  setInstanceCultureName,
  setInstanceTimezone,
} = authSlice.actions;

export default authSlice.reducer;
